import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Copy,
  Download,
  ExternalLink,
  Import,
  Paperclip,
  Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { getAccountQuotes, uploadAccountDocument } from '../api';
import {
  loadColumnFilters,
  saveColumnFilters,
} from '../lib/tableLocalStorage';
import { useColumnState } from '@/hooks/useColumnState';
import { AccountsColumnsMenu } from './AccountsColumnsMenu';
import { AccountsColumnResizeHandle } from './AccountsColumnResizeHandle';
import { useCreateWorkOrderFromQuote } from '@/features/wms/hooks/useWmsWorkOrders';
import { CreateFromQuoteModal } from '@/features/wms/components/work-orders/CreateFromQuoteModal';
import { useCreateQuote, useUpdateQuote, useQuoteV4 } from '@/features/quotes/hooks/useQuotes';
import { QuoteFormModal } from '@/features/quotes/components/modals';
import { InvoiceFormModal } from '@/features/invoices/components/modals';
// PG-only path — quote→invoice association comes from `quotes.id ↔ invoices.quote_id`
// (PostgreSQL), so we load via V4 and translate to the modal's V3-shaped contract.
import { getInvoiceV4, updateInvoice } from '@/features/invoices/api';
import { transformV4InvoiceToV3 } from '@/features/invoices/types';
import type { Invoice, UpdateInvoiceInput } from '@/features/invoices/types';
import { useAccountMembersForPicker } from '../hooks/useSubscriptionManagers';
import ReassignDialog from './ReassignDialog';
import ImportQuotesModal from './ImportQuotesModal';
import type { QuoteDetail, AccountDashboardData, PaginatedResponse } from '../types';
import type { CreateFromQuoteInput } from '@/features/wms/types';
import type { CreateQuoteInput, UpdateQuoteInput } from '@/features/quotes/types';

// =============================================================================
// Column Definition
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T, helpers: QuoteRowHelpers) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

interface QuoteRowHelpers {
  onEdit: (quote: QuoteDetail) => void;
  onCreateWorkOrder: (quote: QuoteDetail) => void;
  onReassign: (quote: QuoteDetail) => void;
  onUploadPO: (quote: QuoteDetail) => void;
  onOpenInvoice: (invoicePgId: string) => void;
  navigate: (path: string) => void;
}

const STATUS_LABELS: Record<QuoteDetail['status'], string> = {
  preparation: 'Preparation',
  sent: 'Sent',
  'waiting-po': 'Waiting PO',
  approved: 'Approved',
  inprogress: 'In Progress',
  'invoice-ready': 'Invoice Ready',
  'partially-invoiced': 'Partial Invoice',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
  expired: 'Expired',
};

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({
  label,
  value: value as QuoteDetail['status'],
}));

const statusVariantMap: Record<QuoteDetail['status'], 'secondary' | 'warning' | 'success' | 'outline' | 'destructive'> = {
  preparation: 'secondary',
  sent: 'warning',
  'waiting-po': 'warning',
  approved: 'success',
  inprogress: 'warning',
  'invoice-ready': 'success',
  'partially-invoiced': 'warning',
  invoiced: 'success',
  cancelled: 'outline',
  rejected: 'destructive',
  expired: 'outline',
};

const POSTGRES_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function getPostgresQuoteId(quote: QuoteDetail): string | null {
  return POSTGRES_UUID_PATTERN.test(quote.id) ? quote.id : null;
}

const COLUMNS: ColumnDef<QuoteDetail>[] = [
  {
    key: 'quoteNumber',
    label: 'Quote #',
    sortKey: 'quoteNumber',
    filterType: 'text',
    getValue: (q) => q.quoteNumber,
    render: (q) => <span className="font-medium">{q.quoteNumber}</span>,
  },
  {
    key: 'name',
    label: 'Name / Project',
    sortKey: 'name',
    filterType: 'text',
    getValue: (q) => q.projectName || q.name || '',
    render: (q) => {
      const displayName = q.projectName || q.name || '(No name)';
      // Show the project name in full — wrap onto multiple lines instead of
      // truncating. `break-words` so very long single tokens still wrap, and
      // `whitespace-normal` overrides the body cell's default nowrap.
      return (
        <span className="block whitespace-normal break-words leading-snug">
          {displayName}
        </span>
      );
    },
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    filterOptions: STATUS_OPTIONS,
    getValue: (q) => q.status,
    render: (q, { navigate }) => {
      const statusVariant = statusVariantMap[q.status] || 'secondary';
      const isExpiringSoon =
        q.status === 'sent' &&
        q.expirationDate &&
        new Date(q.expirationDate) > new Date() &&
        new Date(q.expirationDate) <= new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
      const isExpired =
        q.status === 'sent' &&
        q.expirationDate &&
        new Date(q.expirationDate) < new Date();

      return (
        <div className="flex items-center gap-2">
          <Badge
            variant={statusVariant}
            className="cursor-pointer hover:opacity-80"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/operations/quotes-pipeline?status=${q.status}&highlight=${q.quoteNumber}`);
            }}
          >
            {STATUS_LABELS[q.status]}
          </Badge>
          {isExpiringSoon && <Badge variant="warning">Expiring soon</Badge>}
          {isExpired && <Badge variant="destructive">Expired</Badge>}
        </div>
      );
    },
  },
  {
    key: 'createdDate',
    label: 'Created',
    sortKey: 'createdDate',
    filterType: 'date-range',
    getValue: (q) => q.createdDate,
    render: (q) => formatDate(q.createdDate),
  },
  {
    key: 'expirationDate',
    label: 'Expires',
    sortKey: 'expirationDate',
    filterType: 'date-range',
    getValue: (q) => q.expirationDate || '',
    render: (q) => {
      if (!q.expirationDate) return '—';
      const isExpired =
        q.status === 'sent' && new Date(q.expirationDate) < new Date();
      return (
        <span className={isExpired ? 'text-destructive font-medium' : ''}>
          {formatDate(q.expirationDate)}
        </span>
      );
    },
  },
  {
    key: 'sentDate',
    label: 'Sent Date',
    sortKey: 'sentDate',
    defaultVisible: false,
    filterType: 'date-range',
    getValue: (q) => q.sentDate || '',
    render: (q) => (q.sentDate ? formatDate(q.sentDate) : '—'),
  },
  {
    key: 'acceptedDate',
    label: 'Accepted Date',
    sortKey: 'acceptedDate',
    defaultVisible: false,
    filterType: 'date-range',
    getValue: (q) => q.acceptedDate || '',
    render: (q) => (q.acceptedDate ? formatDate(q.acceptedDate) : '—'),
  },
  {
    key: 'subtotal',
    label: 'Subtotal',
    sortKey: 'subtotal',
    defaultVisible: false,
    filterType: 'number-range',
    getValue: (q) => parseFloat(q.subtotal) || 0,
    render: (q) => formatCurrency(parseFloat(q.subtotal), q.currency),
    align: 'right',
  },
  {
    key: 'discount',
    label: 'Discount',
    sortKey: 'discount',
    defaultVisible: false,
    filterType: 'number-range',
    getValue: (q) => parseFloat(q.discount) || 0,
    render: (q) => formatCurrency(parseFloat(q.discount), q.currency),
    align: 'right',
  },
  {
    key: 'taxAmount',
    label: 'Tax',
    sortKey: 'taxAmount',
    defaultVisible: false,
    filterType: 'number-range',
    getValue: (q) => parseFloat(q.taxAmount) || 0,
    render: (q) => formatCurrency(parseFloat(q.taxAmount), q.currency),
    align: 'right',
  },
  {
    key: 'total',
    label: 'Total',
    sortKey: 'total',
    filterType: 'number-range',
    getValue: (q) => parseFloat(q.total) || 0,
    render: (q) => (
      <span className="font-medium">{formatCurrency(parseFloat(q.total), q.currency)}</span>
    ),
    align: 'right',
  },
  {
    key: 'salesManagerName',
    label: 'Sales Manager',
    sortKey: 'salesManagerName',
    filterType: 'text',
    getValue: (q) => q.salesManagerName || '',
    render: (q) => q.salesManagerName || '—',
  },
  {
    key: 'contractualManagerName',
    label: 'Contractual Manager',
    sortKey: 'contractualManagerName',
    filterType: 'text',
    getValue: (q) => q.contractualManagerName || '',
    render: (q) => q.contractualManagerName || '—',
  },
  {
    key: 'poNumber',
    label: 'PO #',
    sortKey: 'poNumber',
    filterType: 'text',
    getValue: (q) => q.poNumber || '',
    render: (q, { onUploadPO }) => (
      <div className="flex items-center gap-1.5">
        <span className={q.poNumber ? '' : 'text-muted-foreground'}>
          {q.poNumber || '—'}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUploadPO(q);
          }}
          className="text-muted-foreground hover:text-foreground cursor-pointer"
          title={q.poNumber ? 'Replace PO PDF (saves to DMS + Drive)' : 'Attach PO PDF (saves to DMS + Drive)'}
        >
          <Paperclip className="h-3.5 w-3.5" />
        </button>
      </div>
    ),
  },
  {
    key: 'gdriveLink',
    label: 'Drive',
    noFilter: true,
    getValue: (q) => q.gdriveLink || '',
    render: (q) =>
      q.gdriveLink ? (
        <a
          href={q.gdriveLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-atek-blue hover:underline"
          onClick={(e) => e.stopPropagation()}
          title="Open in Google Drive"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Drive
        </a>
      ) : (
        '—'
      ),
    align: 'center',
  },
  {
    key: 'hubspotLink',
    label: 'HubSpot',
    noFilter: true,
    getValue: (q) => q.hubspotLink || '',
    render: (q) =>
      q.hubspotLink ? (
        <a
          href={q.hubspotLink}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-atek-blue hover:underline"
          onClick={(e) => e.stopPropagation()}
          title="Open in HubSpot"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          HubSpot
        </a>
      ) : (
        '—'
      ),
    align: 'center',
  },
  {
    key: 'invoices',
    label: 'Invoices',
    filterType: 'text',
    // CSV/clipboard export shows the invoice numbers as a comma-separated list.
    getValue: (q) => (q.invoices || []).map((inv) => inv.invoiceNumber).join(', '),
    render: (q, { onOpenInvoice }) => {
      const invs = q.invoices || [];
      if (invs.length === 0) return <span className="text-muted-foreground">—</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {invs.map((inv) => {
            const variant =
              inv.status === 'paid' ? 'success'
                : inv.status === 'sent' ? 'warning'
                : inv.status === 'cancelled' || inv.status === 'void' ? 'outline'
                : 'secondary';
            return (
              <Badge
                key={inv.id}
                variant={variant}
                className="cursor-pointer hover:opacity-80"
                onClick={(e) => {
                  e.stopPropagation();
                  // Open the invoice edit modal inline (V4/PG fetch). No tab
                  // switch — keeps the user on the Quotes tab.
                  onOpenInvoice(inv.id);
                }}
                title={`${inv.status} · ${inv.total}`}
              >
                {inv.invoiceNumber}
              </Badge>
            );
          })}
        </div>
      );
    },
  },
  {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (q, { onEdit, onCreateWorkOrder, onReassign, navigate }) => {
      const pgQuoteId = getPostgresQuoteId(q);
      const hasSyncedReadModel = !!pgQuoteId;
      const syncingTitle = 'Quote is still syncing to PostgreSQL';

      return (
        <div className="flex items-center gap-2">
          {q.mongoQuoteId && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(q);
              }}
              className="text-atek-blue hover:underline text-sm cursor-pointer"
            >
              Edit
            </button>
          )}
          {['approved', 'won', 'inprogress'].includes(q.status) && getPostgresQuoteId(q) && !q.workOrderId && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onCreateWorkOrder(q);
              }}
            >
              Create WO
            </Button>
          )}
          {q.workOrderId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/wms/work-orders/${q.workOrderId}`);
              }}
            >
              View WO
            </Button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!hasSyncedReadModel) return;
              onReassign(q);
            }}
            disabled={!hasSyncedReadModel}
            className={cn(
              hasSyncedReadModel
                ? 'text-muted-foreground hover:text-foreground cursor-pointer'
                : 'text-muted-foreground cursor-not-allowed opacity-50',
            )}
            title={hasSyncedReadModel ? 'Reassign to another account' : syncingTitle}
          >
            <ArrowRightLeft className="h-4 w-4" />
          </button>
        </div>
      );
    },
    align: 'right',
  },
];

// =============================================================================
// localStorage Persistence
// =============================================================================

// `-v2` suffix on visibility/order/widths: the previous QuotesTab persisted only
// visibility under `atek-account-quotes-column-visibility`. We now persist three
// pieces of state via `useColumnState`; bumping the key avoids inheriting a
// stale visibility map from before the new manager/PO/Drive/HubSpot columns
// existed (which would default-hide them for current users).
const VISIBILITY_STORAGE_KEY = 'atek-account-quotes-column-visibility-v2';
const ORDER_STORAGE_KEY = 'atek-account-quotes-column-order-v2';
const WIDTHS_STORAGE_KEY = 'atek-account-quotes-column-widths-v2';
const FILTERS_STORAGE_KEY_PREFIX = 'atek-account-quotes-column-filters';
const DEFAULT_COLUMN_WIDTH = 160;

// =============================================================================
// CSV Export Utilities
// =============================================================================

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  let str = String(value);
  if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function exportToCSV(items: QuoteDetail[], columns: ColumnDef<QuoteDetail>[], filename: string) {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => escapeCSVValue(c.label)).join(',');
  const rows = items.map((item) =>
    exportCols.map((c) => escapeCSVValue(c.getValue(item))).join(','),
  );
  const csvContent = [header, ...rows].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function copyToClipboard(items: QuoteDetail[], columns: ColumnDef<QuoteDetail>[]): Promise<void> {
  const exportCols = columns.filter((c) => !c.noFilter);
  const header = exportCols.map((c) => c.label).join('\t');
  const rows = items.map((item) =>
    exportCols.map((c) => String(c.getValue(item) ?? '')).join('\t'),
  );
  const text = [header, ...rows].join('\n');
  return navigator.clipboard.writeText(text);
}

// =============================================================================
// SortableHeader Component
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: 1 | -1;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter(
  col: ColumnDef<QuoteDetail>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try { return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : []; }
      catch { return []; }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-start font-normal">
              {selected.length ? `${selected.length} selected` : '—'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// QuotesTab Component (Mode B: server-side pagination + client-side refinement)
// =============================================================================

interface QuotesTabProps {
  accountId: string;
  highlightId?: string;
}

// Mirror SensorsTab / TransmittersTab: fetch everything in one page so users
// scroll + filter client-side, no pagination footer.
const PAGE_SIZE = 5000;

export default function QuotesTab({ accountId, highlightId }: QuotesTabProps) {
  const { t } = useTranslation('quotes');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const highlightedRowRef = useRef<HTMLTableRowElement>(null);

  // Server-side state
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [isExportingAll, setIsExportingAll] = useState(false);

  // Client-side column filters (refinement within page)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    () => loadColumnFilters(FILTERS_STORAGE_KEY_PREFIX, accountId),
  );

  // Visibility / order / widths managed by the shared hook (src/hooks/useColumnState.ts)
  // so QuotesTab gets the same drag-to-reorder + drag-to-resize behaviour as
  // AccountsListPage / SensorsTab / TransmittersTab.
  const {
    orderedColumns,
    visibleColumns,
    columnWidths,
    isVisible,
    toggleColumn,
    setColumnOrder,
    setColumnWidth,
  } = useColumnState<ColumnDef<QuoteDetail>>({
    columns: COLUMNS,
    storageKeys: {
      visibility: VISIBILITY_STORAGE_KEY,
      order: ORDER_STORAGE_KEY,
      widths: WIDTHS_STORAGE_KEY,
    },
  });

  const widthFor = (key: string) => columnWidths[key] ?? DEFAULT_COLUMN_WIDTH;
  const totalTableWidth = visibleColumns.reduce(
    (sum, c) => sum + widthFor(c.key),
    0,
  );

  // Client-side sort (within page)
  const [sortKey, setSortKey] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  // Modal states
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);
  const [editingQuoteId, setEditingQuoteId] = useState<string | null>(null);
  const [editingQuotePgId, setEditingQuotePgId] = useState<string | null>(null);
  // Remembers the PG id of a quote created in the open create modal via "Save"
  // (without closing) so a subsequent save UPDATES it instead of creating a
  // duplicate. Reset whenever the create modal opens or closes.
  const createdQuotePgIdRef = useRef<string | null>(null);
  const [workOrderQuote, setWorkOrderQuote] = useState<QuoteDetail | null>(null);
  const [reassignQuote, setReassignQuote] = useState<QuoteDetail | null>(null);
  const [isImportOpen, setIsImportOpen] = useState(false);
  // Selected attached-invoice — drives the InvoiceFormModal when a badge in
  // the new Invoices column is clicked. PG id only (V4 path).
  const [selectedInvoicePgId, setSelectedInvoicePgId] = useState<string | null>(null);

  // Persist column filters to localStorage
  useEffect(() => {
    saveColumnFilters(FILTERS_STORAGE_KEY_PREFIX, accountId, columnFilters);
  }, [columnFilters, accountId]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Extract text column filter values for server-side search
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false;
        if (value.startsWith('[')) return false;
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);


  // Dashboard data for prefill
  const dashboardData = queryClient.getQueryData<AccountDashboardData>(['account-dashboard', accountId]);
  const accountName = dashboardData?.account?.name || 'Current Account';

  // Account members for contractual manager prefill (uses mongoUserId for quote form compatibility)
  const { data: accountMembers } = useAccountMembersForPicker(accountId);
  const contractualManagerMongoId = useMemo(() => {
    // Prefer contract-level contractual manager, fall back to account-level tagged member
    const fromContract = dashboardData?.current_contract?.contractual_manager_ids?.[0];
    if (fromContract) return fromContract;
    const tagged = accountMembers?.find((m) => m.tags?.includes('Contractual Manager'));
    return tagged?.mongoUserId;
  }, [dashboardData?.current_contract?.contractual_manager_ids, accountMembers]);

  const quoteCreatePrefill = {
    accountName: dashboardData?.account?.name,
    accountNumber: dashboardData?.account?.account_number,
    customerId: dashboardData?.account?.mongo_organisation_id || undefined,
    contractId: dashboardData?.current_contract?.id,
    salesManagerId: dashboardData?.current_contract?.sales_manager_id || dashboardData?.account?.sales_manager_id || undefined,
    contractualManagerId: contractualManagerMongoId,
    pgAccountId: accountId,
    lockCustomer: Boolean(dashboardData?.account?.mongo_organisation_id),
  };

  const hasLegacyOrg = !!(dashboardData?.account?.mongo_organisation_id || dashboardData?.account?.organisation_id);

  // Server-side data fetch
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['account-quotes', accountId, page, statusFilter, debouncedColumnSearch],
    queryFn: () =>
      getAccountQuotes(accountId, {
        pageNumber: page,
        pageLength: PAGE_SIZE,
        status: statusFilter === 'all' ? undefined : statusFilter,
        search: debouncedColumnSearch || undefined,
      }),
  });

  const quotes = data?.data || [];
  const total = data?.total || 0;

  // Client-side column filtering (AND-combined, within current page)
  const filteredItems = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return quotes;

    return quotes.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            return selected.length === 0 || selected.includes(String(rawValue || ''));
          } catch { return true; }
        }

        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        return String(rawValue ?? '').toLowerCase().includes(filterValue.toLowerCase());
      }),
    );
  }, [quotes, columnFilters]);

  // Client-side sorting (within filtered page)
  const sortedItems = useMemo(() => {
    if (!sortKey) return filteredItems;
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      const aVal = col.getValue(a);
      const bVal = col.getValue(b);
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * sortDirection;
      }
      return String(aVal).localeCompare(String(bVal)) * sortDirection;
    });
  }, [filteredItems, sortKey, sortDirection]);

  const handleSort = (key: string) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
  };

  // Scroll to highlighted row
  useEffect(() => {
    if (highlightId && highlightedRowRef.current && data) {
      setTimeout(() => {
        highlightedRowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [highlightId, data]);

  // Mutations
  const createWorkOrderMutation = useCreateWorkOrderFromQuote();
  const createQuoteMutation = useCreateQuote();
  const updateQuoteMutation = useUpdateQuote();
  const editingQuoteQuery = useQuoteV4(editingQuotePgId);

  // Handle V4 quote fetch errors
  useEffect(() => {
    if (editingQuoteQuery.error && editingQuotePgId) {
      toast.error('Unable to load quote for editing', {
        description: 'The quote data could not be retrieved.',
      });
      setEditingQuoteId(null);
      setEditingQuotePgId(null);
    }
  }, [editingQuoteQuery.error, editingQuotePgId]);

  const handleQuoteFormSubmit = async (formData: CreateQuoteInput | UpdateQuoteInput) => {
    try {
      // After the first keep-open "Save" creates the quote, later saves must
      // UPDATE it — not create a duplicate. updateQuote() PUTs the PG UUID to
      // /v4/quotes/:id, mirroring handleQuoteEditSubmit.
      if (createdQuotePgIdRef.current) {
        await updateQuoteMutation.mutateAsync({
          id: createdQuotePgIdRef.current,
          data: formData as UpdateQuoteInput,
        });
        queryClient.invalidateQueries({ queryKey: ['account-quotes', accountId] });
        queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
        return;
      }

      // Inject the PG account UUID — required by the V4-native POST
      // /v4/quotes endpoint that createQuote now targets. The legacy
      // `customer` Mongo id stays in the payload too for the response
      // translator that still emits a V3Quote shape.
      const created = await createQuoteMutation.mutateAsync({
        ...formData,
        accountId,
      } as CreateQuoteInput);
      // Remember the new quote's PG id (created._id is the PG UUID for a
      // V4-native quote) so a subsequent keep-open save updates it.
      createdQuotePgIdRef.current = created?._id ?? null;
      // Don't close the modal here — let the QuoteFormModal decide based on
      // which button was clicked (Save vs Save and Close).

      const createdSubtotal = Array.isArray(created.subtotals) && created.subtotals.length > 0
        ? created.subtotals.reduce((sum, subtotal) => sum + (Number(subtotal.total) || 0), 0)
        : Array.isArray(created.skus)
          ? created.skus.reduce((sum, sku) => sum + (Number(sku.total) || 0), 0)
          : Number(created.total) || 0;
      const createdTotal = Number(created.total) || createdSubtotal;
      const subtotalValue = createdSubtotal.toFixed(2);
      const totalValue = createdTotal.toFixed(2);

      // Optimistic cache update: inject the new quote immediately so the user
      // sees it in the table without waiting for CDC sync (MongoDB → PostgreSQL).
      const now = new Date().toISOString();
      const optimisticQuote: QuoteDetail = {
        id: `pending:${created._id}`,
        quoteNumber: created.quote_number,
        name: created.project_name || undefined,
        projectName: created.project_name || undefined,
        status: (created.status || 'preparation') as QuoteDetail['status'],
        createdDate: now.split('T')[0],
        expirationDate: created.expiration_date || undefined,
        sentDate: undefined,
        acceptedDate: undefined,
        subtotal: subtotalValue,
        discount: '0.00',
        taxAmount: '0.00',
        total: totalValue,
        currency: created.currency || 'CAD',
        mongoQuoteId: created._id,
        createdAt: now,
        updatedAt: now,
      };

      queryClient.setQueriesData<PaginatedResponse<QuoteDetail>>(
        { queryKey: ['account-quotes', accountId] },
        (old) => old ? { ...old, data: [optimisticQuote, ...old.data], total: old.total + 1 } : old,
      );

      // Background invalidation — CDC will sync eventually, refreshing with real V4 data
      queryClient.invalidateQueries({ queryKey: ['account-quotes', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });

      // Return the created quote so the form can attach sensors that were
      // staged before the quote (and its server-assigned number) existed.
      return created;
    } catch {
      // Error toast is handled by the mutation's onError callback
      // Modal stays open so the user can retry
    }
  };

  const handleQuoteEditSubmit = async (formData: CreateQuoteInput | UpdateQuoteInput) => {
    // Prefer the PG UUID so V4 read paths (Kanban, Print, lists) reflect the
    // change immediately. updateQuote() routes UUID → PG and falls back to the
    // Mongo id for quotes not yet materialized in PostgreSQL.
    const updateId = editingQuotePgId || editingQuoteId;
    if (!updateId) return;
    try {
      await updateQuoteMutation.mutateAsync({ id: updateId, data: formData as UpdateQuoteInput });
      // Don't close the modal here — let the QuoteFormModal decide based on
      // which button was clicked (Save vs Save and Close).
      queryClient.invalidateQueries({ queryKey: ['account-quotes', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
    } catch {
      // Error toast is handled by the mutation's onError callback
    }
  };

  const handleCreateWorkOrder = async (quoteId: string, input: CreateFromQuoteInput) => {
    const result = await createWorkOrderMutation.mutateAsync({ quoteId, data: input });
    navigate(`/wms/work-orders/${result.id}`);
  };

  const handleExportAllQuotes = async () => {
    try {
      setIsExportingAll(true);
      const allQuotes: QuoteDetail[] = [];
      const batchSize = 100;
      let pageNum = 0;
      let totalCount = 0;

      do {
        const batch = await getAccountQuotes(accountId, {
          pageNumber: pageNum,
          pageLength: batchSize,
          status: statusFilter === 'all' ? undefined : statusFilter,
        });
        allQuotes.push(...(batch.data || []));
        totalCount = batch.total ?? 0;
        pageNum++;
      } while (allQuotes.length < totalCount);

      exportToCSV(allQuotes, COLUMNS, 'account-quotes-all');
      toast.success(`Exported ${allQuotes.length} quotes`);
    } catch (e) {
      console.error('Export all quotes failed:', e);
      toast.error('Failed to export all quotes');
    } finally {
      setIsExportingAll(false);
    }
  };

  // PO PDF upload — single hidden <input type="file"> shared by every row. We
  // remember which quote was clicked in a ref so the change handler knows where
  // to attach the file. Saves to the account's Drive folder AND to the DMS
  // (tagged with quote_id + document_type=purchase_order) via the existing
  // /v4/accounts/:id/upload-document endpoint.
  const poFileInputRef = useRef<HTMLInputElement>(null);
  const poUploadQuoteRef = useRef<QuoteDetail | null>(null);

  const handlePOFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const quote = poUploadQuoteRef.current;
    // Reset the input so the same file can be re-selected later.
    e.target.value = '';
    poUploadQuoteRef.current = null;
    if (!file || !quote) return;

    const pgId = getPostgresQuoteId(quote);
    if (!pgId) {
      toast.error('Quote is still syncing to PostgreSQL — try again in a moment');
      return;
    }

    try {
      toast.loading(`Uploading ${file.name} to Drive + DMS…`, { id: 'po-upload' });
      await uploadAccountDocument(accountId, file, {
        entityType: 'quote',
        entityId: pgId,
        documentType: 'purchase_order',
      });
      toast.success(`PO attached to quote ${quote.quoteNumber}`, { id: 'po-upload' });
      queryClient.invalidateQueries({ queryKey: ['account-quotes', accountId] });
      queryClient.invalidateQueries({ queryKey: ['attachments', 'quote', pgId] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      toast.error(`PO upload failed: ${msg}`, { id: 'po-upload' });
    }
  };

  // Fetch the selected attached-invoice via V4 (PG) and adapt to the modal's
  // V3 shape. Mirrors InvoicesTab's pattern but without the Mongo fallback —
  // the badge always carries a PG UUID since the join is PG-side.
  const { data: selectedInvoice, isLoading: selectedInvoiceLoading } = useQuery({
    queryKey: ['invoice-detail-v4', selectedInvoicePgId],
    queryFn: async (): Promise<Invoice> => {
      const v4 = await getInvoiceV4(selectedInvoicePgId!);
      const invoice = transformV4InvoiceToV3(v4);
      const dashData = queryClient.getQueryData<{
        account: { name: string };
        current_contract?: { id: string; contract_number: string; name: string };
      }>(['account-dashboard', accountId]);
      if (dashData?.account) {
        invoice.customer = { _id: accountId, name: dashData.account.name };
      }
      if (typeof invoice.contract === 'string' && dashData?.current_contract) {
        invoice.contract = {
          _id: dashData.current_contract.id,
          contract_number: dashData.current_contract.contract_number,
          name: dashData.current_contract.name,
        };
      }
      return invoice;
    },
    enabled: !!selectedInvoicePgId,
  });

  const updateSelectedInvoiceMutation = useMutation({
    mutationFn: ({ invoiceId, data: updateData }: { invoiceId: string; data: UpdateInvoiceInput }) =>
      updateInvoice(invoiceId, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-quotes', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-invoices', accountId] });
      queryClient.invalidateQueries({ queryKey: ['account-dashboard', accountId] });
      setSelectedInvoicePgId(null);
      toast.success('Invoice updated');
    },
    onError: (error) => {
      toast.error('Failed to update invoice', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    },
  });

  // Row helpers for column renderers
  const rowHelpers: QuoteRowHelpers = {
    onEdit: (q) => {
      const pgId = getPostgresQuoteId(q);
      if (!pgId) {
        toast.error('Quote is still syncing to PostgreSQL');
        return;
      }
      setEditingQuoteId(q.mongoQuoteId || null);
      setEditingQuotePgId(pgId);
    },
    onCreateWorkOrder: setWorkOrderQuote,
    onReassign: setReassignQuote,
    onUploadPO: (q) => {
      poUploadQuoteRef.current = q;
      poFileInputRef.current?.click();
    },
    onOpenInvoice: (invoicePgId) => setSelectedInvoicePgId(invoicePgId),
    navigate,
  };

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center space-y-4">
          <p className="text-destructive">Error loading quotes</p>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </p>
          <Button variant="outline" onClick={() => refetch()}>
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Quotes</CardTitle>
            <CardDescription>
              {filteredItems.length < quotes.length
                ? `Showing ${filteredItems.length} of ${quotes.length} on this page`
                : `Showing ${quotes.length} of ${total}`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px] h-8">
              <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                copyToClipboard(sortedItems, COLUMNS);
                toast.success('Copied to clipboard');
              }}
            >
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExportingAll}>
                  <Download className="h-4 w-4 mr-1" />
                  {isExportingAll ? 'Exporting...' : 'CSV'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export Quotes</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    exportToCSV(sortedItems, COLUMNS, 'account-quotes');
                    toast.success(`Exported ${sortedItems.length} rows`);
                  }}
                >
                  Export Page ({sortedItems.length} rows)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportAllQuotes}>
                  Export All Quotes ({total})
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <AccountsColumnsMenu
              orderedColumns={orderedColumns}
              isVisible={isVisible}
              onToggle={toggleColumn}
              onReorder={setColumnOrder}
            />
            {hasLegacyOrg && (
              <Button
                variant="default"
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setIsImportOpen(true)}
              >
                <Import className="h-4 w-4 mr-1" />
                Import Quotes
              </Button>
            )}
            <Button size="sm" onClick={() => { createdQuotePgIdRef.current = null; setIsQuoteFormOpen(true); }}>
              <Plus className="h-4 w-4 mr-1" />
              {t('page.newQuote')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table
            // table-layout: fixed + explicit colgroup so drag-to-resize widths
            // apply to every row (filter row, label row, body rows). Mirrors
            // the AccountsListPage / SensorsTab pattern.
            style={{
              tableLayout: 'fixed',
              width: `${totalTableWidth}px`,
            }}
          >
            <colgroup>
              {visibleColumns.map((col) => (
                <col key={col.key} style={{ width: `${widthFor(col.key)}px` }} />
              ))}
            </colgroup>
            <TableHeader>
              {/* ROW 1: Column filters (always visible) */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => renderColumnFilter(col, columnFilters, setColumnFilter))}
              </TableRow>

              {/* ROW 2: Column labels + sort + resize handles */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(
                      'relative py-1.5 px-2',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                    )}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                    {!col.noFilter && (
                      <AccountsColumnResizeHandle
                        columnKey={col.key}
                        currentWidth={widthFor(col.key)}
                        onResize={setColumnWidth}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading && Array.from({ length: 10 }).map((_, i) => (
                <TableRow key={i}>
                  {visibleColumns.map((col) => (
                    <TableCell key={col.key}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))}

              {/* Empty state */}
              {!isLoading && sortedItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-muted-foreground">
                    No quotes found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading && sortedItems.map((quote) => (
                <TableRow
                  key={quote.id}
                  ref={quote.id === highlightId ? highlightedRowRef : undefined}
                  className={cn(
                    'hover:bg-muted/50 cursor-pointer',
                    quote.id === highlightId && 'bg-yellow-50 dark:bg-yellow-950/30 animate-pulse-once',
                  )}
                  onClick={() => rowHelpers.onEdit(quote)}
                >
                  {visibleColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      className={cn(
                        // overflow-hidden + min-w-0 keep long manager names /
                        // project names from pushing the column wider than the
                        // colgroup-defined width under table-layout:fixed.
                        'text-sm py-1.5 px-2 overflow-hidden align-top min-w-0',
                        col.align === 'right' && 'text-right tabular-nums',
                        col.align === 'center' && 'text-center',
                        col.className,
                      )}
                    >
                      {col.render
                        ? col.render(quote, rowHelpers)
                        : (String(col.getValue(quote) ?? '') || '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

      </CardContent>

      {/* Quote Create Modal */}
      <QuoteFormModal
        open={isQuoteFormOpen}
        onOpenChange={(open) => { if (!open) createdQuotePgIdRef.current = null; setIsQuoteFormOpen(open); }}
        quote={null}
        onSubmit={handleQuoteFormSubmit}
        isLoading={createQuoteMutation.isPending}
        initialCustomerData={quoteCreatePrefill}
      />

      {/* Quote Edit Modal */}
      <QuoteFormModal
        open={!!editingQuoteQuery.data}
        onOpenChange={(open) => {
          if (!open) { setEditingQuoteId(null); setEditingQuotePgId(null); }
        }}
        v4Quote={editingQuoteQuery.data || null}
        pgQuoteId={editingQuotePgId || undefined}
        onSubmit={handleQuoteEditSubmit}
        isLoading={updateQuoteMutation.isPending}
        initialCustomerData={{
          ...quoteCreatePrefill,
          lockCustomer: true,
        }}
      />

      {/* Create Work Order Modal */}
      <CreateFromQuoteModal
        open={!!workOrderQuote}
        onClose={() => setWorkOrderQuote(null)}
        onSubmit={handleCreateWorkOrder}
        quote={workOrderQuote ? {
          id: workOrderQuote.id,
          quoteNumber: workOrderQuote.quoteNumber,
          projectName: workOrderQuote.projectName || workOrderQuote.name,
          total: formatCurrency(parseFloat(workOrderQuote.total), workOrderQuote.currency),
        } : null}
        accountId={accountId}
        accountName={accountName}
        isLoading={createWorkOrderMutation.isPending}
      />

      {/* Reassign Dialog */}
      <ReassignDialog
        entityType="quote"
        entityId={reassignQuote?.id ?? ''}
        entityLabel={reassignQuote?.quoteNumber ?? ''}
        currentAccountId={accountId}
        currentAccountName={accountName}
        open={!!reassignQuote}
        onOpenChange={(open) => { if (!open) setReassignQuote(null); }}
      />

      {/* Import Quotes Modal */}
      <ImportQuotesModal
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        accountId={accountId}
        accountName={accountName}
      />

      {/* Hidden file input wired to the per-row PO paperclip button */}
      <input
        ref={poFileInputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handlePOFileSelected}
      />

      {/* Attached-invoice edit modal — opened when a badge in the Invoices
          column is clicked. V4/PG fetch path; mirrors InvoicesTab. */}
      <InvoiceFormModal
        open={!!selectedInvoicePgId}
        onOpenChange={(open) => { if (!open) setSelectedInvoicePgId(null); }}
        invoice={selectedInvoiceLoading ? null : (selectedInvoice || null)}
        onSubmit={async (updateData) => {
          if (selectedInvoice) {
            await updateSelectedInvoiceMutation.mutateAsync({
              invoiceId: selectedInvoice._id,
              data: updateData,
            });
          }
        }}
        isSubmitting={updateSelectedInvoiceMutation.isPending}
      />
    </Card>
  );
}
